import "./CustomerInsightsChart.scss";
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { KnowYourCustomerData } from "@blisspointmedia/bpm-types/dist/KnowYourCustomer";
import { formatNumber, formatPercent } from "../utils/format-utils";
import {
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields,
} from "./customerInsightsConstants";
import { buildColorMap, getChartType } from "./customerInsightsUtils";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import ChartContainer from "../Components/ChartContainer";
import { ChartContainerRightActions, CustomerInsightsLegend } from "./CustomerInsightsGroup";

interface StackedBarProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: JSX.Element;
  decimalPrecision: number;
}

const StackedBar: React.FC<StackedBarProps> = ({
  data,
  fieldPrettyName,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision,
}) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const barMax = Math.max(
    ...data.map(val => val.clientPopulation),
    ...data.map(val => val.tuPopulation)
  );
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const barFormattedData = useMemo(() => {
    return data.map(({ clientPopulation, tuPopulation, levels }) => ({
      name: levels,
      val1: Math.round(clientPopulation),
      val2: Math.round(tuPopulation),
      val2Reduced: Math.round(tuPopulation - clientPopulation),
      computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision),
    }));
  }, [data, decimalPrecision]);

  const legendFormattedData = useMemo(() => {
    return barFormattedData.map(({ name, val1, val2, computedValue }) => ({
      name: name,
      val1: formatNumber(val1),
      val2: formatNumber(val2),
      computedValue: computedValue,
    }));
  }, [barFormattedData]);

  return (
    <div className="customerInsightsBarChart penetrationBarChart">
      <ChartContainer
        title={fieldPrettyName}
        enableHoverDesign
        rightActions={chartContainerRightActions}
      >
        {barFormattedData.map((entry, index) => {
          return (
            <ResponsiveContainer width="100%" height={48} key={entry.name}>
              <BarChart
                className={cn({
                  hoveredValue: entry.name === hoveredValue,
                  unhoveredValue: Boolean(hoveredValue) && entry.name !== hoveredValue,
                })}
                data={[entry]}
                layout="vertical"
                onClick={() => setSelectedValue(entry.name)}
                onMouseEnter={() => {
                  setHoveredValue(entry.name);
                }}
                onMouseLeave={() => {
                  setHoveredValue(undefined);
                }}
              >
                <XAxis type="number" domain={[0, barMax]} hide />
                <YAxis dataKey="name" type="category" hide />
                <Legend
                  content={() => (
                    <CustomerInsightsLegend
                      data={[legendFormattedData[index]]}
                      hoveredValue={hoveredValue}
                      setHoveredValue={setHoveredValue}
                    />
                  )}
                  layout="vertical"
                  align="left"
                />
                <Bar
                  dataKey="val1"
                  stackId="a"
                  fill={colorMap[entry.name]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="val2Reduced"
                  stackId="a"
                  fill={colorMap[entry.name]}
                  isAnimationActive={false}
                  style={{ opacity: 0.25 }}
                />
              </BarChart>
            </ResponsiveContainer>
          );
        })}
      </ChartContainer>
    </div>
  );
};

interface PenetrationPieProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: JSX.Element;
  decimalPrecision: number;
}

const PenetrationPie: React.FC<PenetrationPieProps> = ({
  data,
  fieldPrettyName,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision,
}) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const innerPieFormattedData = useMemo(() => {
    return data.map(({ levels, tuPopulation }) => {
      return {
        name: levels,
        value: Math.round(tuPopulation),
      };
    });
  }, [data]);

  const outerPieFormattedData = useMemo(() => {
    const formattedData: { name; value }[] = [];

    data.forEach(({ levels, clientPopulation, tuPopulation }) => {
      formattedData.push({
        name: levels,
        value: Math.round(clientPopulation),
      });
      formattedData.push({
        name: `non-${levels}`,
        value: Math.round(tuPopulation - clientPopulation),
      });
    });

    return formattedData;
  }, [data]);

  const legendFormattedData = useMemo(() => {
    return data.map(({ levels, clientPopulation, tuPopulation }) => {
      return {
        name: levels,
        val1: formatNumber(clientPopulation, 0),
        val2: formatNumber(tuPopulation, 0),
        computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision),
        color: colorMap[levels],
      };
    });
  }, [colorMap, data, decimalPrecision]);

  return (
    <div className="customerInsightsPieChart penetrationPieChart">
      <ChartContainer
        title={fieldPrettyName}
        enableHoverDesign
        rightActions={chartContainerRightActions}
      >
        <ResponsiveContainer width="100%">
          <PieChart>
            <Legend
              content={() => (
                <CustomerInsightsLegend
                  data={legendFormattedData}
                  hoveredValue={hoveredValue}
                  setHoveredValue={setHoveredValue}
                  setSelectedValue={setSelectedValue}
                />
              )}
              layout="vertical"
              align="left"
              verticalAlign="top"
            />
            <Pie
              className="penetrationInnerPie"
              data={innerPieFormattedData}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              cx="50%"
              cy="50%"
              innerRadius="20%"
              outerRadius="80%"
              dataKey="value"
              onMouseEnter={slice => {
                setHoveredValue(slice?.name);
              }}
              onMouseLeave={() => {
                setHoveredValue(undefined);
              }}
            >
              {innerPieFormattedData.map(({ name }) => (
                <Cell
                  className={cn("pieCell", {
                    hoveredValue: name === hoveredValue,
                    unhoveredValue: Boolean(hoveredValue) && name !== hoveredValue,
                  })}
                  key={`cell-${name}`}
                  fill={colorMap[name]}
                  onClick={() => setSelectedValue(name)}
                  onMouseEnter={() => {
                    setHoveredValue(name);
                  }}
                  onMouseLeave={() => {
                    setHoveredValue(undefined);
                  }}
                />
              ))}
            </Pie>
            <Pie
              className="penetrationOuterPie"
              data={outerPieFormattedData}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              cx="50%"
              cy="50%"
              innerRadius="20%"
              outerRadius="90%"
              dataKey="value"
            >
              {outerPieFormattedData.map(({ name }) => (
                <Cell
                  className={cn("pieCell", {
                    hoveredValue: name === hoveredValue,
                    unhoveredValue: Boolean(hoveredValue) && name !== hoveredValue,
                  })}
                  key={`cell-${name}`}
                  fill={colorMap[name] ?? "transparent"}
                  onClick={() =>
                    setSelectedValue(name.startsWith("non-") ? name.substring(4) : name)
                  }
                  onMouseEnter={() => {
                    setHoveredValue(name.startsWith("non-") ? name.substring(4) : name);
                  }}
                  onMouseLeave={() => {
                    setHoveredValue(undefined);
                  }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};

interface SelectedPieProps {
  data: KnowYourCustomerData[];
  fieldName: string;
  fieldPrettyName: string;
  selectedValue: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: JSX.Element;
  decimalPrecision: number;
}

const SelectedPie: React.FC<SelectedPieProps> = ({
  data,
  fieldName,
  fieldPrettyName,
  selectedValue,
  setSelectedValue,
  chartContainerRightActions,
  decimalPrecision,
}) => {
  const pieRef: RefObject<HTMLDivElement> = useRef(null);
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const innerPieFormattedData = useMemo(() => {
    const formattedData: { name; value }[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.tuPopulation),
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const outerPieFormattedData = useMemo(() => {
    const formattedData: { name; value }[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.clientPopulation),
      });
      formattedData.push({
        name: `non-${selectedData.levels}`,
        value: Math.round(selectedData.tuPopulation - selectedData.clientPopulation),
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const legendFormattedData = useMemo(() => {
    return data.map(({ levels, clientPopulation, tuPopulation }) => {
      return {
        name: levels,
        val1: formatNumber(clientPopulation, 0),
        val2: formatNumber(tuPopulation, 0),
        computedValue: formatPercent(clientPopulation / tuPopulation, decimalPrecision),
        color:
          getChartType(fieldName) === "pie"
            ? levels === selectedValue
              ? colorMap[levels]
              : "#DFE2E6"
            : undefined,
      };
    });
  }, [colorMap, data, decimalPrecision, fieldName, selectedValue]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (!(pieRef && pieRef.current)) {
        return;
      }

      const legend = pieRef.current.querySelector(".recharts-legend-wrapper .legend");
      const innerPie = pieRef.current.querySelector(".recharts-pie.penetrationInnerPie");
      const outerPie = pieRef.current.querySelector(".recharts-pie.penetrationOuterPie");

      const withinLegend = legend?.contains(event.target);
      const withinInnerPie = innerPie?.contains(event.target);
      const withinOuterPie = outerPie?.contains(event.target);

      if (!(withinLegend || withinInnerPie || withinOuterPie)) {
        setSelectedValue(undefined);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [setSelectedValue]);

  return (
    <div className="customerInsightsPieChart selectedPenetrationPieChart" ref={pieRef}>
      <ChartContainer
        title={fieldPrettyName}
        enableHoverDesign
        rightActions={chartContainerRightActions}
      >
        <ResponsiveContainer width="100%">
          <PieChart>
            <Legend
              content={() => (
                <CustomerInsightsLegend
                  data={legendFormattedData}
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                />
              )}
              layout="vertical"
              align="left"
              verticalAlign="top"
            />
            <Pie
              className="penetrationInnerPie"
              data={innerPieFormattedData}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              cx="50%"
              cy="50%"
              innerRadius="20%"
              outerRadius="80%"
              dataKey="value"
              onClick={() => setSelectedValue(undefined)}
            >
              {innerPieFormattedData.map(({ name }) => (
                <Cell className="pieCell" key={`cell-${name}`} fill={colorMap[name]} />
              ))}
            </Pie>
            <Pie
              className="penetrationOuterPie"
              data={outerPieFormattedData}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              cx="50%"
              cy="50%"
              innerRadius="20%"
              outerRadius="90%"
              dataKey="value"
              onClick={() => setSelectedValue(undefined)}
            >
              {outerPieFormattedData.map(({ name }) => (
                <Cell
                  className="pieCell"
                  key={`cell-${name}`}
                  fill={colorMap[name] ?? "transparent"}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};

interface PenetrationChartProps {
  fieldName: CustomerInsightsFields;
  data: KnowYourCustomerData[];
  decimalPrecision?: number;
}

const PenetrationChart: React.FC<PenetrationChartProps> = ({
  fieldName,
  data,
  decimalPrecision = 1,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const fieldPrettyName = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];

  if (selectedValue) {
    return (
      <SelectedPie
        data={data}
        fieldName={fieldName}
        fieldPrettyName={fieldPrettyName}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={Math.round(decimalPrecision)}
      />
    );
  }

  if (getChartType(fieldName) === "bar") {
    return (
      <StackedBar
        data={data}
        fieldPrettyName={fieldPrettyName}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={Math.round(decimalPrecision)}
      />
    );
  }

  if (getChartType(fieldName) === "pie") {
    return (
      <PenetrationPie
        data={data}
        fieldPrettyName={fieldPrettyName}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
        decimalPrecision={Math.round(decimalPrecision)}
      />
    );
  }

  return null;
};

export default PenetrationChart;
